import { isEmpty, startCase } from 'lodash';
import pluralize from 'pluralize';
import type { ReactElement } from 'react';

import { PostsBlock } from '@/blocks/posts-block';
import { Author } from '@/components/author';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { Container } from '@/components/container';
import { ContentMatrix } from '@/components/content-matrix';
import { Heading } from '@/components/heading';
import { Image } from '@/components/image';
import { Text } from '@/components/text';
import { Pill } from '@/components/ui/pill';
import { AllPostsPostInterface, PostInterface, PostSnippets } from '@/types/cms-types';
import { formatDateForUser } from '@/utils/format';
import { transformPostType } from '@/utils/strings';
import { tv } from '@/utils/styles';

export interface ExtendedPostInterface extends PostInterface {
  posts?: PostSnippets;
  relatedPosts?: AllPostsPostInterface[];
}

interface PostDetailViewProps {
  page: ExtendedPostInterface;
  backPath: string;
}

const PostDetailView = ({ page, backPath }: PostDetailViewProps): ReactElement => {
  const {
    spacer,
    grid,
    breadcrumbs,
    heading,
    publishedDate,
    featuredImage,
    summary,
    blocks,
    sidebar,
    categoriesHeading,
    categoryPill,
    relatedPosts,
  } = styles();

  return (
    <>
      <span className={spacer()} />
      <Container>
        <div className={grid()}>
          <div>
            <Breadcrumbs
              items={[
                {
                  label: `Back to ${pluralize(startCase(transformPostType(page?.postType)))} listing`,
                  href: backPath,
                },
              ]}
              textClassName="lg:text-base"
              className={breadcrumbs()}
              variant="blue"
            />

            <Heading variant="h1" className={heading()}>
              {page?.title}
            </Heading>

            <Text className={publishedDate()}>Published at {formatDateForUser(page?.publishedAt)}</Text>

            <Image data={page?.image} className={featuredImage()} />

            <div className={summary()} dangerouslySetInnerHTML={{ __html: page?.summary }} />

            <div className={blocks()}>
              <ContentMatrix blocks={page?.blocks} />
            </div>
          </div>
          <div className={sidebar()}>
            <Author author={page?.author} />
            {page?.postTopic && (
              <>
                <Heading variant="h4" as="h2" className={categoriesHeading()}>
                  Categories
                </Heading>
                <Pill label={page?.postTopic?.label} className={categoryPill()} />
              </>
            )}
          </div>
        </div>
      </Container>

      {!isEmpty(page?.relatedPosts) && page.relatedPosts && (
        <PostsBlock
          id="related-posts"
          heading="Related Articles"
          posts={page.relatedPosts}
          postType={page?.postType}
          className={relatedPosts()}
        />
      )}
    </>
  );
};

const styles = tv({
  slots: {
    grid: 'mx-auto gap-16 max-xl:max-w-screen-md xl:grid xl:grid-cols-[2.3fr_1fr]',
    spacer: 'block bg-blue-500 pt-20 lg:pt-[calc(var(--page-header-height)+theme(spacing.2))]',
    breadcrumbs: 'mb-3 mt-4 border-b-white bg-white lg:mb-5 lg:mt-8',
    heading: 'text-blue-800 xl:!text-[40px]',
    publishedDate: 'mt-4 text-grey-500',
    featuredImage: 'mt-2 lg:mt-6',
    summary: 'mb-0 mt-8 text-baseLg text-blue-800 lg:text-md lg:leading-snug last:[&_p]:mb-0',
    blocks:
      '-mx-5 md:-mx-6 lg:-mx-8 xl:pb-12 [&_.block-heading]:tracking-[0] lg:[&_.block-heading]:text-[32px] [&_.block-rich-text]:text-base xl:[&_.blocks_section]:!py-12 [&_.rich-text-inner]:w-full [&_.rich-text]:max-w-none',
    sidebar: 'xl:mt-12',
    categoriesHeading: 'mb.4 text-blue-800',
    categoryPill: 'inline-flex',
    relatedPosts: 'xl:pt-4',
  },
});

export { PostDetailView };
